// Import from Ready — onboarding front door. Lists the connected applicant's Ready reporting
// projects and one-click imports any not yet in Build (creates a Ready-linked bead_project).
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type RP = { id: string; name: string; grant_program: string; imported: boolean };

export default function ImportFromReadyModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [flash, setFlash] = useState<Record<string, string>>({});
  const { data, isLoading, error } = useQuery<{ projects: RP[] }>({
    queryKey: ['ready-reporting-projects'],
    queryFn: () => api.get('/ready/reporting-projects'),
    retry: false,
  });
  const imp = useMutation({
    mutationFn: (id: string) => api.post('/ready/import-project', { ready_project_id: id }),
    onSuccess: (r: any, id) => {
      setFlash(f => ({ ...f, [id]: r?.ok ? 'Imported ✓' : (r?.error === 'AlreadyImported' ? 'Already in Build' : (r?.error || 'Failed')) }));
      qc.invalidateQueries({ queryKey: ['ready-reporting-projects'] });
      qc.invalidateQueries({ queryKey: ['bead-projects'] });
    },
    onError: (e: any, id) => setFlash(f => ({ ...f, [id]: 'Failed: ' + (e?.message || e) })),
  });

  const pull = useMutation({
    mutationFn: (id: string) => api.post('/ready/import-locations', { ready_project_id: id }),
    onSuccess: (r: any, id) => { setFlash(f => ({ ...f, [id]: r?.ok ? `Pulled ${r.inserted} BSLs (${r.awarded_bsl_count} total)` : (r?.error || 'Failed') })); qc.invalidateQueries({ queryKey: ['bead-projects'] }); },
    onError: (e: any, id) => setFlash(f => ({ ...f, [id]: 'BSL pull failed: ' + (e?.message || e) })),
  });
  const projects = data?.projects ?? [];
  const remaining = projects.filter(p => !p.imported).length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg, #fff)', borderRadius: 12, width: 'min(680px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Import projects from Ready</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Your awarded BEAD projects from the state portal. Import creates a Ready-linked project in Build.</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>
        <div style={{ padding: 16, overflow: 'auto' }}>
          {isLoading && <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 12 }}>Loading your Ready projects…</div>}
          {error && (
            <div style={{ padding: 14, background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: 13 }}>
              Couldn't reach Ready. Make sure the Ready.net integration is connected (Settings → Integrations), then try again.
            </div>
          )}
          {!isLoading && !error && projects.length === 0 && (
            <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13 }}>No reporting projects found for this Ready account.</div>
          )}
          {projects.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>{projects.length} projects · {remaining} not yet imported</div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {projects.map((p, i) => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderTop: i ? '1px solid #f1f5f9' : 'none', background: p.imported ? '#f8fafc' : '#fff' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{p.grant_program}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {flash[p.id] && <span style={{ fontSize: 11, color: flash[p.id].startsWith('Fail') ? '#ef4444' : '#10b981' }}>{flash[p.id]}</span>}
                      {p.imported ? (
                        <><span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>✓ In Build</span><button disabled={pull.isPending} onClick={() => pull.mutate(p.id)} style={{ marginLeft: 8, padding: '5px 10px', background: '#0891b2', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{pull.isPending ? '…' : 'Pull BSLs'}</button></>
                      ) : (
                        <button disabled={imp.isPending} onClick={() => imp.mutate(p.id)}
                          style={{ padding: '6px 14px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          {imp.isPending ? '…' : 'Import'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
